import { memo } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import '../SystemMasters.css';
import type { SubBomAttribute, SubBomCardAccess, SubBomCardHandlers, SubBomOptionErrors } from './types';

const MAX_LABEL_LENGTH = 25;

const MAX_OPTION_LENGTH = 50;

interface SubBomDropdownCardProps extends SubBomCardHandlers, SubBomCardAccess {
  item: SubBomAttribute;
  index: number;
  error?: string | undefined;
  optionErrors?: SubBomOptionErrors | undefined;
  isOpen: boolean;
  onToggleOpen: (index: number) => void;
  onAddOption: (index: number) => void;
  onOptionNameChange: (index: number, optionIndex: number, value: string) => void;
}

/**
 * One dropdown or multi-dropdown attribute card. Markup, classes and labels
 * mirror the BOM Custom Attributes dropdown card: the option rows are always
 * rendered and shown or hidden by the `dropdown-items show` class rather than
 * being mounted and unmounted, and an empty card shows the same "no options"
 * line.
 *
 * Unlike that card, permissions arrive as props instead of being resolved from
 * the store inside every card, the component is memoised, and each validation
 * message sits outside its flex row — `.dropdown-container` and
 * `.freetext-container` are `align-items: flex-end`, so a message inside the
 * input column would drag the action icons down onto its line.
 * @param {SubBomDropdownCardProps} props - Card props.
 * @returns {ReactElement} The rendered card.
 */
function SubBomDropdownCard({
  item,
  index,
  error,
  optionErrors,
  isOpen,
  onToggleOpen,
  onNameChange,
  onAddOption,
  onOptionNameChange,
  onToggleActive,
  onDelete,
  canEdit,
  canDelete,
}: SubBomDropdownCardProps): ReactElement {
  const { t } = useTranslation(['systemMaster', 'common']);
  const options = item?.options ?? [];
  const optionsClassName = isOpen ? 'dropdown-items show' : 'dropdown-items ';

  return (
    <div className="d-flex align-items-center justify-content-center">
      <div className="card listing-card mx-12 p-2 mb-2 flex-fill">
        <div className="dropdown-container">
          <div className="input-container">
            <div className="input-group form-floating-label fixed-label">
              <input
                name="freeLabel"
                autoFocus={!item?._id}
                value={item?.keyName ?? ''}
                placeholder={t('common:DROP_DOWN_CARD.ENTER_LABEL')}
                type="text"
                maxLength={MAX_LABEL_LENGTH}
                onChange={(event) => onNameChange(index, event.target.value)}
                className="form-control"
                disabled={!canEdit}
              />
              <label className="asterisk-req">
                {t('common:DROP_DOWN_CARD.LABEL')}
                <span>*</span>
              </label>
            </div>
          </div>
          <div className="input-action d-flex">
            {item?._id && canEdit && (
              <CustomTooltip placement="bottom" tooltipText={t('common:CUSTOM_MODULE.ACTIVE_INACTIVE')}>
                <button
                  type="button"
                  className=" mr-0"
                  aria-label={item?.isActive ? 'Active' : 'Inactive'}
                  onClick={() => onToggleActive(item._id as string, null, !item.isActive)}
                >
                  <span className={item.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                </button>
              </CustomTooltip>
            )}
            {canEdit && (
              <CustomTooltip placement="bottom" tooltipText={t('common:CUSTOM_ATTRIBUTES.ADD_VALUE')}>
                <button type="button" className=" mr-0" aria-label="Add option" onClick={() => onAddOption(index)}>
                  <span className="icon-add" />
                </button>
              </CustomTooltip>
            )}
            <CustomTooltip placement="bottom" tooltipText={t('common:CUSTOM_ATTRIBUTES.ADD_OPTION')}>
              <button
                type="button"
                className="position-relative mr-0"
                aria-label="Toggle options"
                onClick={() => onToggleOpen(index)}
              >
                <span
                  className={
                    isOpen ? 'icon-new-down-arrow-icon icon-dropdown icon-inverse' : 'icon-new-down-arrow-icon icon-dropdown'
                  }
                />
              </button>
            </CustomTooltip>
            {canDelete && (
              <CustomTooltip placement="bottom" tooltipText={t('common:CUSTOM_ATTRIBUTES.DELETE')}>
                <button
                  type="button"
                  className=" mr-0"
                  aria-label="Delete attribute"
                  onClick={() => onDelete(index, item?._id ?? null, null)}
                >
                  <span className="icon-Delete f-24" />
                </button>
              </CustomTooltip>
            )}
          </div>
        </div>
        {error && <label className="text-danger m-0 p-1">{t(error)}</label>}

        {options.length > 0 ? (
          options.map((option, optionIndex) => (
            <div key={option?._id ?? `new-option-${optionIndex}`} className={optionsClassName}>
              <div className="freetext-container mt-2 mx-0">
                <div className="input-container">
                  <div className="input-group form-floating-label fixed-label">
                    <input
                      name="freeLabel"
                      autoFocus={!option?._id}
                      disabled={!canEdit}
                      value={option?.keyName ?? ''}
                      placeholder={t('common:DROP_DOWN_CARD.ENTER_VALUE')}
                      type="text"
                      maxLength={MAX_OPTION_LENGTH}
                      onChange={(event) => onOptionNameChange(index, optionIndex, event.target.value)}
                      className="form-control"
                    />
                    <label className="asterisk-req">
                      {t('common:DROP_DOWN_CARD.VALUE')} {optionIndex + 1}
                      <span>*</span>
                    </label>
                  </div>
                </div>
                <div className="input-action d-flex">
                  {option?._id && canEdit && (
                    <CustomTooltip placement="top" tooltipText={t('common:CUSTOM_MODULE.ACTIVE_INACTIVE')}>
                      <button
                        type="button"
                        aria-label="Toggle option"
                        className=" mr-0"
                        onClick={() => onToggleActive(item._id as string, option._id as string, !option.isActive)}
                      >
                        <span className={option.isActive ? 'icon-Eye-Open' : 'icon-Eye-Closed'} />
                      </button>
                    </CustomTooltip>
                  )}
                  {canDelete && (
                    <CustomTooltip placement="top" tooltipText={t('common:TEXT_CARD.DELETE')}>
                      <button
                        className=" mr-0"
                        type="button"
                        aria-label="Delete option"
                        onClick={() => onDelete(index, item?._id ?? null, { optionIndex, optionId: option?._id ?? null })}
                      >
                        <span className="icon-Delete f-24" />
                      </button>
                    </CustomTooltip>
                  )}
                </div>
              </div>
              {optionErrors?.[optionIndex] && (
                <label className="text-danger m-0 p-1">{t(optionErrors[optionIndex] as string)}</label>
              )}
            </div>
          ))
        ) : (
          <div className={optionsClassName}>
            <p className="mt-3 mb-0 text-center">{t('common:DROP_DOWN_CARD.NO_OPTIONS_FOUND')}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(SubBomDropdownCard);
